/**
 * Hub de Solicitações do colaborador — 5 cartões grandes (um por tipo)
 * + lista "Minhas solicitações" (assistências + requisições mescladas,
 * mais recentes primeiro). O backend já escopa a lista para as próprias
 * solicitações quando o usuário não é gestor.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiCamera, FiChevronRight } from "react-icons/fi";

import { useAuth } from "../../../context/AuthContext";
import {
  listarAssistencias, listarRequisicoes, podeAcaoTipo, SITUACAO_LABELS,
  SITUACAO_TONES, TIPOS_SOLICITACAO,
} from "../../../services/solicitacoes";
import { PERM_RQC_CRIAR, PERM_RQC_VER, listarMeusRqc } from "../../../services/rqc";
import { SITUACAO_TONE as SITUACAO_TONE_COMPRA } from "../../../services/requisicoesCompraUi";
import { PageContainer, PageTitle, PageTopBar, Section, SectionTitle } from "../../../styles/shared";
import { formatarDataRelativa, unwrapLista } from "./utils";
import {
  ErroBox, ListaCartoes, Pill, SolCard, SolCardTopo, SolMeta, SolNumero,
  TipoCard, TipoHint, TipoLabel, TipoSigla, TipoTextos, VazioBox,
} from "./styles";
import { Carregando } from "../_shared/Estados";

export default function Hub() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  // Só mostra os tipos que o usuário pode CRIAR (RBAC granular por tipo).
  const tiposCriaveis = TIPOS_SOLICITACAO.filter((t) =>
    podeAcaoTipo(hasPermission, "create", t.value),
  );
  // RQC (ADR-0074): permissao propria — o escritorio pode ter SO ela, sem
  // nenhuma das solicitacoes da producao.
  const podeCriarRqc = hasPermission(PERM_RQC_CRIAR);
  const podeVerRqc = hasPermission(PERM_RQC_VER);
  const podeVerSolicitacoes = TIPOS_SOLICITACAO.some((t) => podeAcaoTipo(hasPermission, "view", t.value));

  const [minhas, setMinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      const [assistencias, requisicoes, rqcs] = await Promise.all([
        // Com as juntadas: quem pediu vê para onde foram as peças/itens.
        podeVerSolicitacoes ? listarAssistencias(request, { incluir_juntadas: 1 }) : [],
        podeVerSolicitacoes ? listarRequisicoes(request, { incluir_juntadas: 1 }) : [],
        podeVerRqc ? listarMeusRqc(request) : [],
      ]);
      const lista = [
        ...unwrapLista(assistencias).map((s) => ({ ...s, familia: "assistencia" })),
        ...unwrapLista(requisicoes).map((s) => ({ ...s, familia: "requisicao" })),
        ...unwrapLista(rqcs).map((s) => ({ ...s, familia: "rqc" })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setMinhas(lista);
    } catch (e) {
      setErro(e?.message || "Não foi possível carregar suas solicitações.");
    } finally {
      setCarregando(false);
    }
  }, [request, podeVerSolicitacoes, podeVerRqc]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <PageContainer>
      <PageTopBar>
        <PageTitle>Solicitações</PageTitle>
      </PageTopBar>

      {tiposCriaveis.length > 0 || podeCriarRqc ? (
        <ListaCartoes>
          {tiposCriaveis.map((t) => (
            <TipoCard
              key={t.value}
              type="button"
              onClick={() => navigate(`criar/${t.value.toLowerCase()}`)}
            >
              <TipoSigla>{t.value}</TipoSigla>
              <TipoTextos>
                <TipoLabel>{t.label}</TipoLabel>
                <TipoHint>{t.hint}</TipoHint>
              </TipoTextos>
              <FiChevronRight size={20} color="var(--ink-3)" />
            </TipoCard>
          ))}
          {podeCriarRqc ? (
            <TipoCard type="button" onClick={() => navigate("rqc/novo")}>
              <TipoSigla>RQC</TipoSigla>
              <TipoTextos>
                <TipoLabel>Requisição de Compras</TipoLabel>
                <TipoHint>Papel, toner, equipamento e o que mais o escritório precisar comprar</TipoHint>
              </TipoTextos>
              <FiChevronRight size={20} color="var(--ink-3)" />
            </TipoCard>
          ) : null}
        </ListaCartoes>
      ) : (
        <VazioBox>
          Você não tem permissão para criar solicitações. Fale com a gestão
          se precisar de acesso.
        </VazioBox>
      )}

      <Section>
        <SectionTitle>Minhas solicitações</SectionTitle>

        {erro && <ErroBox>{erro}</ErroBox>}

        {!erro && carregando && <Carregando />}

        {!erro && !carregando && minhas.length === 0 && (
          <VazioBox>
            Nenhuma solicitação ainda.
            <br />
            Toque num cartão acima para criar a primeira.
          </VazioBox>
        )}

        {!erro && !carregando && minhas.length > 0 && (
          <ListaCartoes>
            {minhas.map((s) => {
              if (s.familia === "rqc") {
                return (
                  <SolCard key={`rqc-${s.id}`} type="button" onClick={() => navigate(`rqc/${s.id}`)}>
                    <SolCardTopo>
                      <SolNumero>{s.requisicao?.numero || "RQC"}</SolNumero>
                      <Pill $tone={SITUACAO_TONE_COMPRA[s.requisicao?.situacao] || "neutral"}>
                        {s.requisicao?.situacao_label || "Excluída"}
                      </Pill>
                    </SolCardTopo>
                    <SolMeta>
                      <span>RQC</span>
                      <span>{s.motivo_label}</span>
                      {s.prioridade === "URGENTE" ? <span>Urgente</span> : null}
                    </SolMeta>
                    <SolMeta>
                      <span>{formatarDataRelativa(s.created_at)}</span>
                      <span>{s.itens.length} item(ns)</span>
                    </SolMeta>
                  </SolCard>
                );
              }
              const contagem = s.pecas_count ?? s.itens_count ?? 0;
              const rotuloContagem =
                s.familia === "assistencia"
                  ? `${contagem} peça(s)`
                  : `${contagem} item(ns)`;
              return (
                <SolCard
                  key={`${s.familia}-${s.id}`}
                  type="button"
                  onClick={() => navigate(`${s.familia}/${s.id}`)}
                >
                  <SolCardTopo>
                    <SolNumero>
                      {s.numero}
                      {/* Autorizada, recusada ou atendida desde a última vez que você abriu. */}
                      {s.tem_novidade && (
                        <span
                          role="img"
                          aria-label="Novidade: a situação mudou"
                          title="A situação mudou desde a última vez que você abriu"
                          style={{
                            display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                            background: "var(--accent)", marginLeft: 6, verticalAlign: "middle",
                          }}
                        />
                      )}
                    </SolNumero>
                    <Pill $tone={SITUACAO_TONES[s.situacao] || "neutral"}>
                      {SITUACAO_LABELS[s.situacao] || s.situacao}
                    </Pill>
                  </SolCardTopo>
                  <SolMeta>
                    <span>{s.tipo}</span>
                    {s.cliente_nome && <span>{s.cliente_nome}</span>}
                    {s.num_pedido && <span>Pedido {s.num_pedido}</span>}
                    {s.juntada_na_numero && <span>Juntada na {s.juntada_na_numero}</span>}
                  </SolMeta>
                  <SolMeta>
                    <span>{formatarDataRelativa(s.created_at)}</span>
                    <span>{rotuloContagem}</span>
                    {s.tem_fotos && (
                      <span aria-label="Tem fotos">
                        <FiCamera size={13} style={{ verticalAlign: "-2px" }} /> fotos
                      </span>
                    )}
                  </SolMeta>
                </SolCard>
              );
            })}
          </ListaCartoes>
        )}
      </Section>
    </PageContainer>
  );
}
